(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-bleequp-product-switch-root]';
  const STICKY_SELECTOR = '[data-bleequp-product-switch-sticky]';
  const OPTION_LINK_SELECTOR = 'king-linked-options-product a.lv-option-item[data-handle][href]';
  const LINKED_OPTION_ROOT_SELECTOR = 'king-linked-options-product';
  const STYLE_OPTION_NAME = 'Style';
  const GALLERY_DATA_SELECTOR = '[data-bleequp-product-gallery-data]';
  const METADATA_SELECTOR = '[data-bleequp-product-switch-meta]';
  const PREVIEW_QUERY_PARAMETERS = ['preview_theme_id', 'preview_token'];
  const CACHE_TTL = 5 * 60 * 1000;
  const MAX_CACHE_ENTRIES = 3;

  class BleeqUpProductSwitcher {
    constructor() {
      this.cache = new Map();
      this.styleValueCache = new Map();
      this.linkedOptionSyncTimers = new WeakMap();
      this.requestVersion = 0;

      this.onPopState = this.onPopState.bind(this);
      this.onRootClick = this.onRootClick.bind(this);
      this.onRootIntent = this.onRootIntent.bind(this);

      window.addEventListener('popstate', this.onPopState);
      this.initialize(document);
    }

    initialize(scope) {
      const roots = [];

      if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
        roots.push(scope);
      }

      if (scope.querySelectorAll) {
        roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
      }

      roots.forEach((root) => this.initializeRoot(root));
    }

    initializeRoot(root) {
      if (root.dataset.bleequpProductSwitchBound === 'true') {
        return;
      }

      root.dataset.bleequpProductSwitchBound = 'true';
      root.addEventListener('click', this.onRootClick, true);
      root.addEventListener('pointerover', this.onRootIntent, { passive: true });
      root.addEventListener('focusin', this.onRootIntent);
      root.addEventListener('touchstart', this.onRootIntent, { passive: true });

      if (root.dataset.bleequpProductSwitchSyncStyles !== 'false') {
        this.syncLinkedOptionStyles(root);
        this.observeLinkedOptionStyles(root);
      }
      this.initializeDescriptionToggles(root);
      this.scheduleIdlePrefetch(root);
    }

    getActiveRoot() {
      return document.querySelector(ROOT_SELECTOR);
    }

    getGroup(root) {
      try {
        const handles = JSON.parse(root.dataset.bleequpProductSwitchGroup || '[]');

        return Array.isArray(handles)
          ? handles.map((handle) => String(handle).trim()).filter(Boolean)
          : [];
      } catch (_error) {
        return [];
      }
    }

    getTargetFromLink(link, root) {
      if (!link || !root || link.hasAttribute('download') || link.target === '_blank') {
        return null;
      }

      const url = this.createTargetUrl(link.href);
      const handle = link.dataset.handle || this.getProductHandle(url);
      const currentHandle = root.dataset.bleequpProductSwitchCurrentHandle;

      if (
        url.origin !== window.location.origin ||
        !handle ||
        handle === currentHandle ||
        !this.getGroup(root).includes(handle)
      ) {
        return null;
      }

      return {
        handle,
        sectionId: this.getTargetSectionId(root, handle),
        url,
        fetchMode: root.dataset.bleequpProductSwitchFetchMode || 'section',
        replaceMode: root.dataset.bleequpProductSwitchReplaceMode || 'root'
      };
    }

    getTargetFromUrl(url, root) {
      const targetUrl = new URL(url, window.location.origin);
      const handle = this.getProductHandle(targetUrl);

      if (!handle || !this.getGroup(root).includes(handle)) {
        return null;
      }

      return {
        handle,
        sectionId: this.getTargetSectionId(root, handle),
        url: targetUrl,
        fetchMode: root.dataset.bleequpProductSwitchFetchMode || 'section',
        replaceMode: root.dataset.bleequpProductSwitchReplaceMode || 'root'
      };
    }

    getTargetSectionId(root, handle) {
      const currentSectionId = root.dataset.bleequpProductSwitchSectionId;
      const rawMap = root.dataset.bleequpProductSwitchSectionMap;

      if (!rawMap) {
        return currentSectionId;
      }

      try {
        const sectionMap = JSON.parse(rawMap);

        return sectionMap?.[handle] || currentSectionId;
      } catch (_error) {
        return currentSectionId;
      }
    }

    createTargetUrl(href) {
      const targetUrl = new URL(href, window.location.origin);
      const currentUrl = new URL(window.location.href);

      PREVIEW_QUERY_PARAMETERS.forEach((parameter) => {
        if (!targetUrl.searchParams.has(parameter) && currentUrl.searchParams.has(parameter)) {
          targetUrl.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
        }
      });

      return targetUrl;
    }

    getProductHandle(url) {
      const pathParts = url.pathname.split('/').filter(Boolean);
      const productIndex = pathParts.indexOf('products');

      return productIndex === -1 ? '' : decodeURIComponent(pathParts[productIndex + 1] || '');
    }

    getOptionLink(event) {
      return event.target instanceof Element ? event.target.closest(OPTION_LINK_SELECTOR) : null;
    }

    onRootClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = this.getOptionLink(event);
      const target = this.getTargetFromLink(link, event.currentTarget);

      if (!target) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.switchProduct(target, {
        updateHistory: true,
        restoreFocus: document.activeElement === link
      });
    }

    onRootIntent(event) {
      const target = this.getTargetFromLink(this.getOptionLink(event), event.currentTarget);

      if (target) {
        this.prefetch(target);
      }
    }

    onPopState() {
      const root = this.getActiveRoot();

      if (!root) {
        return;
      }

      const target = this.getTargetFromUrl(window.location.href, root);

      if (!target) {
        const currentHandle = this.getProductHandle(new URL(window.location.href));

        if (currentHandle !== root.dataset.bleequpProductSwitchCurrentHandle) {
          window.location.assign(window.location.href);
        }

        return;
      }

      if (target.handle !== root.dataset.bleequpProductSwitchCurrentHandle) {
        this.switchProduct(target, { updateHistory: false, restoreFocus: false });
      }
    }

    scheduleIdlePrefetch(root) {
      if (root.dataset.bleequpProductSwitchPrefetchScheduled === 'true' || !this.canPrefetch()) {
        return;
      }

      root.dataset.bleequpProductSwitchPrefetchScheduled = 'true';

      const warm = () => {
        if (document.visibilityState === 'hidden') {
          return;
        }

        Array.from(root.querySelectorAll(OPTION_LINK_SELECTOR))
          .map((link) => this.getTargetFromLink(link, root))
          .filter(Boolean)
          .forEach((target, index) => {
            window.setTimeout(() => this.prefetch(target), index * 200);
          });
      };

      const schedule = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(warm, { timeout: 4000 });
        } else {
          window.setTimeout(warm, 1200);
        }
      };

      if (document.readyState === 'complete') {
        schedule();
      } else {
        window.addEventListener('load', schedule, { once: true });
      }
    }

    canPrefetch() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

      return !connection || (!connection.saveData && !['slow-2g', '2g'].includes(connection.effectiveType));
    }

    prefetch(target) {
      this.getSectionHtml(target).catch(() => {
      });
    }

    async switchProduct(target, options) {
      const root = this.getActiveRoot();

      if (!root || (!target.sectionId && target.fetchMode !== 'page')) {
        window.location.assign(target.url.href);
        return;
      }

      const requestVersion = ++this.requestVersion;
      this.setBusy(root, true);

      try {
        const html = await this.getSectionHtml(target);

        if (requestVersion !== this.requestVersion) {
          return;
        }

        const next = this.parseSection(html, target);

        if (!next) {
          throw new Error('Target product section was not found.');
        }

        this.promotePrimaryMedia(next.root);
        this.cleanupRoot(root);
        const activeRoot = this.replaceTemplateContent(root, next.root, target) || this.replaceRoot(root, next.root);
        this.replaceAuxiliaryContent(next.document);

        if (options.updateHistory) {
          history.pushState(
            { bleequpProductSwitch: true, handle: target.handle },
            '',
            this.toRelativeUrl(target.url)
          );
        }

        this.syncMetadata(next.metadata);
        this.reinitializeProductUi(activeRoot, next.galleryData, target);
        this.initializeRoot(activeRoot);

        if (options.restoreFocus) {
          this.restoreOptionFocus(activeRoot, target.handle);
        }
      } catch (_error) {
        if (requestVersion === this.requestVersion) {
          window.location.assign(target.url.href);
        }
      } finally {
        if (requestVersion === this.requestVersion) {
          this.setBusy(this.getActiveRoot(), false);
        }
      }
    }

    getSectionHtml(target) {
      const requestUrl = new URL(target.url.href);

      if (target.fetchMode === 'page') {
        requestUrl.searchParams.delete('section_id');
      } else {
        requestUrl.searchParams.set('section_id', target.sectionId);
      }

      requestUrl.hash = '';

      const cacheKey = requestUrl.href;
      const cached = this.cache.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.promise;
      }

      const promise = fetch(requestUrl.href, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Product section request failed with ${response.status}.`);
          }

          return response.text();
        })
        .catch((error) => {
          this.cache.delete(cacheKey);
          throw error;
        });

      this.cache.set(cacheKey, { promise, timestamp: Date.now() });
      this.trimCache();

      return promise;
    }

    trimCache() {
      while (this.cache.size > MAX_CACHE_ENTRIES) {
        this.cache.delete(this.cache.keys().next().value);
      }
    }

    syncLinkedOptionStyles(root) {
      const links = this.getLinkedOptionLinks(root);

      if (!links.length) {
        return;
      }

      const valuesByHandle = new Map();
      const currentHandle = root.dataset.bleequpProductSwitchCurrentHandle;
      const currentStyle = root.dataset.bleequpProductSwitchCurrentStyle;

      if (currentHandle && currentStyle) {
        valuesByHandle.set(currentHandle, currentStyle);
        this.styleValueCache.set(currentHandle, Promise.resolve(currentStyle));
      }

      this.applyLinkedOptionStyles(root, valuesByHandle);

      const pendingHandles = Array.from(new Set(links.map((link) => link.dataset.handle).filter(Boolean)))
        .filter((handle) => !valuesByHandle.has(handle));

      if (!pendingHandles.length) {
        return;
      }

      Promise.all(
        pendingHandles.map((handle) => this.getProductStyleValue(handle).then((value) => [handle, value]))
      ).then((entries) => {
        entries.forEach(([handle, value]) => {
          if (value) {
            valuesByHandle.set(handle, value);
          }
        });

        this.applyLinkedOptionStyles(root, valuesByHandle);
      }).catch(() => {
      });
    }

    getLinkedOptionLinks(root) {
      const optionRoot = root.querySelector(LINKED_OPTION_ROOT_SELECTOR);

      if (!optionRoot) {
        return [];
      }

      const styleBox = Array.from(optionRoot.querySelectorAll('.lv-option-box')).find((box) => {
        return (box.dataset.optionName || '').toLowerCase() === STYLE_OPTION_NAME.toLowerCase();
      });

      return Array.from((styleBox || optionRoot).querySelectorAll('a.lv-option-item[data-handle][href]'));
    }

    getProductStyleValue(handle) {
      if (this.styleValueCache.has(handle)) {
        return this.styleValueCache.get(handle);
      }

      const promise = fetch(this.getProductJsonUrl(handle), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Product JSON request failed with ${response.status}.`);
          }

          return response.json();
        })
        .then((product) => this.getStyleValueFromProduct(product))
        .catch(() => '');

      this.styleValueCache.set(handle, promise);

      return promise;
    }

    getProductJsonUrl(handle) {
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const productIndex = pathParts.indexOf('products');
      const localePrefix = productIndex > 0 ? `/${pathParts.slice(0, productIndex).join('/')}` : '';
      const url = new URL(`${localePrefix}/products/${encodeURIComponent(handle)}.js`, window.location.origin);

      PREVIEW_QUERY_PARAMETERS.forEach((parameter) => {
        const value = new URL(window.location.href).searchParams.get(parameter);

        if (value) {
          url.searchParams.set(parameter, value);
        }
      });

      return url.href;
    }

    getStyleValueFromProduct(product) {
      const options = Array.isArray(product?.options) ? product.options : [];
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      let optionIndex = options.findIndex((option) => {
        const name = typeof option === 'string' ? option : option?.name;

        return name === STYLE_OPTION_NAME;
      });

      if (optionIndex < 0) {
        optionIndex = 0;
      }

      const option = options[optionIndex];

      if (option && typeof option === 'object' && Array.isArray(option.values) && option.values[0]) {
        return String(option.values[0]);
      }

      const variant = variants.find((candidate) => candidate.available) || variants[0];

      if (!variant) {
        return '';
      }

      return String(variant[`option${optionIndex + 1}`] || variant.options?.[optionIndex] || '');
    }

    applyLinkedOptionStyles(root, valuesByHandle) {
      if (!valuesByHandle.size) {
        return;
      }

      this.syncLinkedOptionProductGroupData(root, valuesByHandle);

      const links = this.getLinkedOptionLinks(root);

      links.forEach((link) => {
        const value = valuesByHandle.get(link.dataset.handle);

        if (!value) {
          return;
        }

        const previousValue = link.dataset.value || link.getAttribute('aria-label') || '';

        this.setDataValue(link, 'value', value);
        this.setAttributeValue(link, 'aria-label', value);
        this.replaceExactText(link, previousValue, value);

        const wrapper = link.closest('.lv-option-wrapper');

        if (wrapper) {
          this.setDataValue(wrapper, 'name', value);
          this.setAttributeValue(wrapper, 'title', value);
        }

        link.querySelectorAll('img[alt]').forEach((image) => {
          const alt = image.getAttribute('alt') || '';

          if (!alt || alt === previousValue || alt.startsWith(`${STYLE_OPTION_NAME} - `) || alt.startsWith(`${STYLE_OPTION_NAME} – `)) {
            this.setAttributeValue(image, 'alt', `${STYLE_OPTION_NAME} - ${value}`);
          }
        });
      });

      const currentHandle = root.dataset.bleequpProductSwitchCurrentHandle;
      const currentValue = valuesByHandle.get(currentHandle);

      if (currentValue) {
        root.querySelectorAll('.lv-option-box').forEach((box) => {
          if ((box.dataset.optionName || '').toLowerCase() !== STYLE_OPTION_NAME.toLowerCase()) {
            return;
          }

          this.setDataValue(box, 'optionValue', currentValue);
          this.setAttributeValue(box, 'data-option-value', currentValue);
          box.querySelectorAll('.lv-option-label__selected').forEach((label) => {
            this.setTextValue(label, currentValue);
          });
        });
      }
    }

    syncLinkedOptionProductGroupData(root, valuesByHandle) {
      const script = root.querySelector('#lv-option-product-group-data') || document.getElementById('lv-option-product-group-data');

      if (!script || !script.textContent) {
        return;
      }

      let data;

      try {
        data = JSON.parse(script.textContent);
      } catch (_error) {
        return;
      }

      let didUpdate = false;

      const updateNode = (node) => {
        if (Array.isArray(node)) {
          node.forEach(updateNode);
          return;
        }

        if (!node || typeof node !== 'object') {
          return;
        }

        const handle = node.handle || node.product_handle || this.getHandleFromPath(node.url || node.href || '');
        const value = valuesByHandle.get(handle);

        if (handle && value && node.value !== value) {
          node.value = value;
          didUpdate = true;
        }

        Object.values(node).forEach(updateNode);
      };

      updateNode(data);

      if (didUpdate) {
        script.textContent = JSON.stringify(data);
      }
    }

    getHandleFromPath(path) {
      if (!path || typeof path !== 'string') {
        return '';
      }

      try {
        return this.getProductHandle(new URL(path, window.location.origin));
      } catch (_error) {
        return '';
      }
    }

    replaceExactText(container, oldValue, newValue) {
      if (!oldValue || oldValue === newValue || !container.ownerDocument?.createTreeWalker) {
        return;
      }

      const walker = container.ownerDocument.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const textNodes = [];

      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      textNodes.forEach((node) => {
        if (node.nodeValue.trim() === oldValue) {
          node.nodeValue = node.nodeValue.replace(oldValue, newValue);
        }
      });
    }

    setTextValue(element, value) {
      if (element.textContent !== value) {
        element.textContent = value;
      }
    }

    setDataValue(element, key, value) {
      if (element.dataset[key] !== value) {
        element.dataset[key] = value;
      }
    }

    setAttributeValue(element, name, value) {
      if (element.getAttribute(name) !== value) {
        element.setAttribute(name, value);
      }
    }

    observeLinkedOptionStyles(root) {
      if (root.bleequpLinkedOptionObserver || typeof MutationObserver === 'undefined') {
        return;
      }

      root.bleequpLinkedOptionObserver = new MutationObserver(() => {
        this.scheduleLinkedOptionStyleSync(root);
      });
      root.bleequpLinkedOptionObserver.observe(root, {
        attributes: true,
        attributeFilter: ['aria-label', 'data-name', 'data-option-value', 'data-value'],
        childList: true,
        subtree: true
      });
    }

    scheduleLinkedOptionStyleSync(root) {
      const pending = this.linkedOptionSyncTimers.get(root);

      if (pending) {
        window.clearTimeout(pending);
      }

      const timer = window.setTimeout(() => {
        this.linkedOptionSyncTimers.delete(root);
        this.syncLinkedOptionStyles(root);
      }, 50);

      this.linkedOptionSyncTimers.set(root, timer);
    }

    parseSection(html, target) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const root = Array.from(documentFragment.querySelectorAll(ROOT_SELECTOR)).find((candidate) => {
        if (candidate.dataset.bleequpProductSwitchCurrentHandle !== target.handle) {
          return false;
        }

        return target.fetchMode === 'page' || candidate.dataset.bleequpProductSwitchSectionId === target.sectionId;
      });

      if (!root) {
        return null;
      }

      return {
        document: documentFragment,
        root,
        galleryData: this.readJson(root.querySelector(GALLERY_DATA_SELECTOR)),
        metadata: this.readPageMetadata(documentFragment, this.readJson(root.querySelector(METADATA_SELECTOR)))
      };
    }

    replaceRoot(currentRoot, nextRoot) {
      currentRoot.replaceWith(nextRoot);

      return nextRoot;
    }

    replaceTemplateContent(currentRoot, nextRoot, target) {
      if (target.replaceMode !== 'rx-template') {
        return null;
      }

      const currentSection = currentRoot.closest('.shopify-section');
      const nextSection = nextRoot.closest('.shopify-section');
      const currentMain = currentSection?.closest('main');
      const nextMain = nextSection?.closest('main');

      if (!currentSection || !nextSection || !currentMain || !nextMain) {
        return null;
      }

      const nextSections = this.collectTemplateSections(nextSection);

      if (!nextSections.length) {
        return null;
      }

      const currentSections = this.collectTemplateSections(currentSection);
      const importedSections = nextSections.map((section) => document.importNode(section, true));
      const fragment = document.createDocumentFragment();
      let importedRoot = null;

      importedSections.forEach((section) => {
        if (!importedRoot) {
          importedRoot = section.querySelector(ROOT_SELECTOR);
        }

        fragment.append(section);
      });

      currentMain.insertBefore(fragment, currentSection);
      currentSections.forEach((section) => section.remove());
      this.executeScripts(importedSections);

      return importedRoot;
    }

    collectTemplateSections(startSection) {
      const sections = [];
      let section = startSection;

      while (section) {
        if (section.classList?.contains('shopify-section')) {
          sections.push(section);
        }

        section = section.nextElementSibling;
      }

      return sections;
    }

    executeScripts(containers) {
      containers.forEach((container) => {
        container.querySelectorAll('script').forEach((script) => {
          const type = (script.getAttribute('type') || '').trim().toLowerCase();

          if (type && !['text/javascript', 'application/javascript', 'module'].includes(type)) {
            return;
          }

          if (script.src && script.src.includes('bleequp-product-switcher.js')) {
            return;
          }

          const replacement = document.createElement('script');
          Array.from(script.attributes).forEach((attribute) => replacement.setAttribute(attribute.name, attribute.value));
          replacement.text = script.textContent;
          script.replaceWith(replacement);
        });
      });
    }

    replaceAuxiliaryContent(sourceDocument) {
      const currentSticky = document.querySelector(STICKY_SELECTOR);
      const nextSticky = sourceDocument.querySelector(STICKY_SELECTOR);

      if (currentSticky && nextSticky) {
        currentSticky.replaceWith(nextSticky);
      } else if (currentSticky) {
        currentSticky.remove();
      } else if (nextSticky) {
        const activeRoot = this.getActiveRoot();
        activeRoot?.parentElement?.after(nextSticky);
      }

      const currentQuickBuy = document.getElementById('quick-buy-content');
      const nextQuickBuy = sourceDocument.getElementById('quick-buy-content');

      if (currentQuickBuy && nextQuickBuy) {
        currentQuickBuy.replaceWith(nextQuickBuy);
      }
    }

    readJson(element) {
      if (!element) {
        return null;
      }

      try {
        return JSON.parse(element.textContent);
      } catch (_error) {
        return null;
      }
    }

    readPageMetadata(sourceDocument, fallbackMetadata) {
      const metadata = { ...(fallbackMetadata || {}) };
      const title = sourceDocument.querySelector('title')?.textContent?.trim();
      const description = this.readHeadMeta(sourceDocument, 'name', 'description');
      const canonical = sourceDocument.querySelector('link[rel="canonical"]')?.href;
      const image = this.readHeadMeta(sourceDocument, 'property', 'og:image') || this.readHeadMeta(sourceDocument, 'property', 'og:image:url');
      const imageAlt = this.readHeadMeta(sourceDocument, 'property', 'og:image:alt');
      const imageWidth = this.readHeadMeta(sourceDocument, 'property', 'og:image:width');
      const imageHeight = this.readHeadMeta(sourceDocument, 'property', 'og:image:height');
      const price = this.readHeadMeta(sourceDocument, 'property', 'product:price:amount');
      const currency = this.readHeadMeta(sourceDocument, 'property', 'product:price:currency');
      const availability = this.readHeadMeta(sourceDocument, 'property', 'product:availability');

      if (title) {
        metadata.title = title;
      }
      if (description) {
        metadata.description = description;
      }
      if (canonical) {
        metadata.canonical = canonical;
      }
      if (image) {
        metadata.image = image;
      }
      if (imageAlt) {
        metadata.imageAlt = imageAlt;
      }
      if (imageWidth) {
        metadata.imageWidth = imageWidth;
      }
      if (imageHeight) {
        metadata.imageHeight = imageHeight;
      }
      if (price) {
        metadata.price = price;
      }
      if (currency) {
        metadata.currency = currency;
      }
      if (availability) {
        metadata.available = !/out of stock|unavailable|sold out/i.test(availability);
      }

      return metadata;
    }

    readHeadMeta(sourceDocument, attribute, value) {
      return sourceDocument.head?.querySelector(`meta[${attribute}="${value}"]`)?.content?.trim() || '';
    }

    promotePrimaryMedia(root) {
      const primaryImage = root.querySelector('.product-gallery__media:not([hidden]) img');

      if (primaryImage) {
        primaryImage.loading = 'eager';
        primaryImage.fetchPriority = 'high';
      }
    }

    initializeDescriptionToggles(root) {
      root.querySelectorAll('[data-description-collapsible]').forEach((wrapper) => {
        if (wrapper.dataset.descriptionInitialized === 'true') {
          return;
        }

        const toggle = wrapper.querySelector('[data-description-toggle]');
        const content = wrapper.querySelector('[data-description-content]');

        if (!toggle || !content) {
          wrapper.dataset.descriptionInitialized = 'true';
          return;
        }

        const parsedLines = Number.parseInt(wrapper.dataset.lines, 10);
        const lines = Number.isFinite(parsedLines) && parsedLines > 0 ? parsedLines : 3;
        wrapper.style.setProperty('--description-collapsed-lines', lines);

        const updateState = (isExpanded) => {
          wrapper.classList.toggle('is-expanded', isExpanded);
          toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        };

        const refresh = () => {
          const wasExpanded = wrapper.classList.contains('is-expanded');
          updateState(false);
          const collapsedHeight = content.clientHeight;
          updateState(true);
          const expandedHeight = content.clientHeight;
          updateState(wasExpanded);

          const hasOverflow = Math.ceil(expandedHeight) > Math.ceil(collapsedHeight + 1);
          wrapper.classList.toggle('has-toggle', hasOverflow);
          toggle.hidden = !hasOverflow;
        };

        toggle.addEventListener('click', () => {
          updateState(!wrapper.classList.contains('is-expanded'));
        });

        updateState(false);
        wrapper.dataset.descriptionInitialized = 'true';
        wrapper.bleequpDescriptionObserver = new ResizeObserver(refresh);
        wrapper.bleequpDescriptionObserver.observe(content);
        window.requestAnimationFrame(refresh);
      });
    }

    cleanupRoot(root) {
      window.destroyYgProductGalleryV2?.(root);

      root.querySelectorAll('[data-description-collapsible]').forEach((wrapper) => {
        wrapper.bleequpDescriptionObserver?.disconnect();
      });
    }

    reinitializeProductUi(root, galleryData, target) {
      if (galleryData) {
        window.productGalleryData = galleryData;
      }

      this.runOptionalInitializer(() => window.initYgProductGalleryV2?.(root));
      this.runOptionalInitializer(() => window.initProductGalleryTabs?.());
      this.runOptionalInitializer(() => window.initYgProductDescriptionToggles?.(root));
      this.runOptionalInitializer(() => window.initYgStandaloneIconLists?.(root));
      this.runOptionalInitializer(() => window.Shopify?.PaymentButton?.init?.());
      this.runOptionalInitializer(() => window.jdgm?.initializeWidgets?.());
      this.runOptionalInitializer(() => window.jdgm?.refreshWidgets?.());

      document.dispatchEvent(
        new CustomEvent('bleequp:product-switch:complete', {
          detail: { handle: target.handle, url: target.url.href }
        })
      );
    }

    runOptionalInitializer(callback) {
      try {
        callback();
      } catch (_error) {
      }
    }

    syncMetadata(metadata) {
      if (!metadata) {
        return;
      }

      if (metadata.title) {
        document.title = metadata.title;
      }

      this.setMetaByName('description', metadata.description);
      this.setMetaByProperty('og:title', metadata.title);
      this.setMetaByProperty('og:description', metadata.description);
      this.setMetaByProperty('og:url', metadata.canonical);
      this.setMetaByProperty('og:image', metadata.image);
      this.setMetaByProperty('og:image:url', metadata.image);
      this.setMetaByProperty('og:image:secure_url', metadata.image);
      this.setMetaByProperty('og:image:alt', metadata.imageAlt);
      this.setMetaByProperty('og:image:width', metadata.imageWidth);
      this.setMetaByProperty('og:image:height', metadata.imageHeight);
      this.setMetaByProperty('product:price:amount', metadata.price);
      this.setMetaByProperty('product:price:currency', metadata.currency);
      this.setMetaByProperty('product:availability', metadata.available ? 'in stock' : 'out of stock');
      this.setMetaByName('twitter:title', metadata.title);
      this.setMetaByName('twitter:description', metadata.description);
      this.setMetaByName('twitter:image', metadata.image);
      this.setMetaByName('twitter:image:alt', metadata.imageAlt);
      this.setCanonical(metadata.canonical);
    }

    setMetaByName(name, content) {
      if (!content) {
        return;
      }

      let element = document.head.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.name = name;
        document.head.append(element);
      }

      element.content = content;
    }

    setMetaByProperty(property, content) {
      if (!content) {
        return;
      }

      let element = document.head.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.append(element);
      }

      element.content = content;
    }

    setCanonical(href) {
      if (!href) {
        return;
      }

      let element = document.head.querySelector('link[rel="canonical"]');
      if (!element) {
        element = document.createElement('link');
        element.rel = 'canonical';
        document.head.append(element);
      }

      element.href = href;
    }

    restoreOptionFocus(root, handle) {
      const link = Array.from(root.querySelectorAll(OPTION_LINK_SELECTOR)).find(
        (candidate) => candidate.dataset.handle === handle
      );

      window.requestAnimationFrame(() => link?.focus({ preventScroll: true }));
    }

    setBusy(root, isBusy) {
      if (!root) {
        return;
      }

      root.toggleAttribute('aria-busy', isBusy);
      root.dataset.bleequpProductSwitchState = isBusy ? 'loading' : 'idle';
    }

    toRelativeUrl(url) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  }

  const boot = () => {
    if (window.bleequpProductSwitcher) {
      window.bleequpProductSwitcher.initialize(document);
    } else {
      window.bleequpProductSwitcher = new BleeqUpProductSwitcher();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
